import { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import { MdSearch, MdLocationOn, MdOutlinePeople, MdWorkOutline } from "react-icons/md";
import Header from "../../Header";
import LeftNavBar from "../../LeftNavBar";
import PageHeader from "../../PageHeader";
import { PageHeaderName } from "../../../StyledCss/styledComponents";
import { StyledInput, StyledInputContainer, StyledInputGroup } from "../../../StyledCss/styledInputs";
import { getHiringJobs } from "../../../api/services/hroneApi";
import { normalizeJob } from "../../../util/hiringUtils";
import MyReferralsList from "../MyReferralsList";
import { usePermissions } from "../../../redux/slices/permissionsSlice";
import { HIRING_MANAGEMENT_FEATURE_CODES } from "../../../util/featureCodes";
import "../JobListPage/index.css";
import "./index.css";

const TABS = [
  { id: "OPENINGS",     label: "Job Openings" },
  { id: "MY_REFERRALS", label: "My Referrals" },
];

const EmployeeJobListPage = () => {
  const navigate = useNavigate();
  const { hasAnyPermission } = usePermissions();
  const hasHiringManagement = hasAnyPermission(HIRING_MANAGEMENT_FEATURE_CODES);

  const [activeTab, setActiveTab] = useState("OPENINGS");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const orgDetails = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#2563EB";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getHiringJobs({ status: "OPEN" });
        const data = await res.json();
        if (!cancelled) {
          if (data.status) {
            setJobs(data.data.jobs.map(normalizeJob));
          } else {
            toast.error(data.message || "Failed to load open jobs");
          }
        }
      } catch {
        if (!cancelled) toast.error("Failed to load open jobs");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const filteredJobs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter((job) => {
      const haystack = `${job.title} ${job.location} ${job.designation}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [jobs, searchQuery]);

  // hasAnyPermission reads live from Redux, so this fires as soon as a
  // permissions_updated push (see App.js) grants hiring management access —
  // no navigation away and back needed to pick it up. Mirrors the opposite
  // check in JobListPage (redirects back here when management access is
  // lost) so a promoted/demoted role always lands on the right view.
  if (hasHiringManagement) {
    return <Navigate to="/hiring" replace />;
  }

  return (
    <>
      <Header />
      <div className="d-flex" style={{ "--hiring-primary": primaryColor }}>
        <LeftNavBar />
        <div className="page-content-wrapper">
          <PageHeader>
            <PageHeaderName>Job Openings</PageHeaderName>
          </PageHeader>

          <div className="employee-jobs-content-box">
            <div className="hiring-tab-bar">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  style={
                    activeTab === tab.id
                      ? { color: primaryColor, borderBottom: `3px solid ${primaryColor}` }
                      : {}
                  }
                  className={activeTab === tab.id ? "hiring-active-tab" : "hiring-tab"}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div key={activeTab} className="hiring-tab-content-fade">
              {activeTab === "MY_REFERRALS" ? (
                <MyReferralsList />
              ) : (
                <>
                  <div className="employee-jobs-search-box">
                    <MdSearch className="employee-jobs-search-icon" />
                    <StyledInputGroup style={{ margin: 0, width: "100%" }}>
                      <StyledInputContainer style={{ border: "none" }}>
                        <StyledInput
                          style={{ width: "100%", border: "none", boxShadow: "none" }}
                          placeholder="Search open roles by title, location or designation"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </StyledInputContainer>
                    </StyledInputGroup>
                  </div>

                  {loading ? (
                    <div className="employee-jobs-empty">
                      <p>Loading open jobs…</p>
                    </div>
                  ) : filteredJobs.length === 0 ? (
                    <div className="employee-jobs-empty">
                      <p className="employee-jobs-empty-title">No open positions right now</p>
                      <p className="employee-jobs-empty-sub">Check back soon for new openings.</p>
                    </div>
                  ) : (
                    <div className="employee-jobs-grid">
                      {filteredJobs.map((job) => (
                        <div
                          key={job.id}
                          className="employee-job-card"
                          role="button"
                          tabIndex={0}
                          onClick={() => navigate(`/hiring/openings/${job.id}`)}
                          onKeyDown={(e) => e.key === "Enter" && navigate(`/hiring/openings/${job.id}`)}
                        >
                          <div className="employee-job-card-header">
                            <p className="employee-job-card-title">{job.title}</p>
                            <span className="employee-job-card-badge">{job.employmentType}</span>
                          </div>
                          <p className="employee-job-card-dept">{job.designation}</p>
                          <div className="employee-job-card-meta">
                            <span><MdLocationOn className="employee-job-card-meta-icon" />{job.location}</span>
                            <span><MdOutlinePeople className="employee-job-card-meta-icon" />{job.vacancies} {job.vacancies === 1 ? "Vacancy" : "Vacancies"}</span>
                            <span><MdWorkOutline className="employee-job-card-meta-icon" />{job.experience}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmployeeJobListPage;
